import {
    ThumbnailWithLocationNameWithStatelems,
    LineStatElems,
    AreaStatElems,
} from '../../models/query-response';
import {Curve, Point, Area, StatElems} from './elems';
import {
    formatAreaContext,
    formatLineContext,
} from '../../utils/generalHelpers';

type AreaContext = {
    areaId: string,
    taxonomy: string,
};

type LineAction = {
    lineId: string,
    direction: string,
    taxonomy: string,
};

type LineContext = {
    actions: LineAction[],
};

type AreaStatElemWithTaxonomies = {
    taxonomies: Set<string>,
    statelem: AreaStatElems,
};

type LineStateElemWithDirection = LineStatElems & { direction: string };

type LineStatElemWithTaxonomies = {
    taxonomies: Set<string>,
    statelems: Array<LineStateElemWithDirection>,
};

type LabelBounds = {
    id: string,
    boundsPolygon: Array<Point>,
};

type PathWithId = {
    path: Path2D,
    id: string,
};

type MouseOverEntry = {
    bounds: LabelBounds,
    taxonomies: Set<string>,
};

const titleFont = 'sans-serif';
const titleBackgroundColour = '#ffffff';
const titleFontColour = '#000000';
const titleFontSize = 20;

class StatelemsRenderer {
    constructor(
        thumbnail: ThumbnailWithLocationNameWithStatelems,
        canvas: HTMLCanvasElement,
        taxonomiesElement: HTMLDivElement | null,
        setHoveredElem: Function | null = null
    ) {
        if (!thumbnail || !canvas) {
            throw new Error('Thumbnail and canvas are required');
        }

        const {
            url,
            locationName,
            contexts: {lineContexts, areaContexts},
            statElems: {lineStatElems, areaStatElems},
        } = thumbnail;

        // Validate contexts and statElems if needed
        if (!lineContexts || !areaContexts || !lineStatElems || !areaStatElems) {
            throw new Error('Invalid thumbnail data: missing contexts or statElems');
        }

        this.canvas = canvas;
        this.ctx = this.canvas.getContext('2d');
        this.taxonomiesElement = taxonomiesElement;
        this.url = url;
        this.image = null;
        this.locationName = locationName;
        this.mouseOverArray = [];
        this.areaContexts = areaContexts;
        this.lineContexts = lineContexts;
        this.areaStatElems = areaStatElems;
        this.lineStatElems = lineStatElems;
        this.setHoveredElem = setHoveredElem;
    }

    _getAreaContextByIdMap(): Map<string, AreaContext[]> {
        if (!this.areaContextsByIdMap) {
            const areaIgnoreString = 'ignore';
            const map = new Map();
            this.areaContexts.forEach((context) => {
                if (!context.areaId.includes(areaIgnoreString)) {
                    const existing = map.get(context.areaId) ?? [];
                    map.set(context.areaId, [context, ...existing]);
                }
            });
            this.areaContextsByIdMap = map;
        }
        return this.areaContextsByIdMap;
    }

    _getLineContextByIdMap(): Map<string, LineAction[]> {
        if (!this.lineContextsByIdMap) {
            const map = new Map();
            const flattenedActions = this.lineContexts.flatMap((context) => {
                const {actions, ...contextWithoutActions} = context;
                return actions.map((action) => ({
                    ...action,
                    ...contextWithoutActions,
                }));
            });

            flattenedActions.forEach((actionContext) => {
                const existing = map.get(actionContext.lineId) ?? [];
                map.set(actionContext.lineId, [actionContext, ...existing]);
            });

            this.lineContextsByIdMap = map;
        }
        return this.lineContextsByIdMap;
    }

    _updateStatElemInMap<T>(
        map: Map<string, T>,
        id: string,
        formattedTaxonomy: string,
        createNewEntry: () => T,
        updateExistingEntry: (entry: T) => void,
    ): void {
        const existingEntry = map.get(id);
        if (!existingEntry) {
            const newEntry = createNewEntry();
            newEntry.taxonomies = new Set([formattedTaxonomy]);
            map.set(id, newEntry);
        } else {
            existingEntry.taxonomies.add(formattedTaxonomy);
            updateExistingEntry(existingEntry);
        }
    }

    _updateAreaStatElemInMap(
        map: Map<string, AreaStatElemWithTaxonomies>,
        statelem: AreaStatElems,
        areaContext: AreaContext,
    ): void {
        const {area_id} = statelem;
        const {taxonomy} = areaContext;
        const formattedTaxonomy = formatAreaContext(taxonomy);

        this._updateStatElemInMap(
            map,
            area_id,
            formattedTaxonomy,
            () => ({statelem}),
            () => {
            },
        );
    }

    _getAreaStatElemsMap(): Map<string, AreaStatElemWithTaxonomies> {
        if (!this.areaStatElemsByIdMap) {
            const map = new Map();
            this.areaStatElems.forEach((statelem) => {
                const areaContexts =
                    this._getAreaContextByIdMap().get(statelem.area_id) ?? [];
                areaContexts.forEach((areaContext) => {
                    this._updateAreaStatElemInMap(map, statelem, areaContext);
                });
            });
            this.areaStatElemsByIdMap = map;
        }
        return this.areaStatElemsByIdMap;
    }

    _updateLineStatElemInMap(
        map: Map<string, LineStatElemWithTaxonomies>,
        statelem: LineStatElems,
        lineContext: LineAction,
    ): void {
        const {line_id} = statelem;
        const {direction, taxonomy} = lineContext;
        const formattedTaxonomy = formatLineContext(taxonomy);

        this._updateStatElemInMap(
            map,
            line_id,
            formattedTaxonomy,
            () => ({statelems: [{...statelem, direction}]}),
            (entry) => {
                const hasDirection = entry.statelems.some(
                    (elem) => elem.direction === direction,
                );
                if (!hasDirection) {
                    entry.statelems.push({...statelem, direction});
                }
            },
        );
    }

    _getLineStatElemsMap(): Map<string, LineStatElemWithTaxonomies> {
        if (!this.lineStatElemsByIdMap) {
            const map = new Map();
            this.lineStatElems.forEach((statelem) => {
                const lineContexts =
                    this._getLineContextByIdMap().get(statelem.line_id) ?? [];
                lineContexts.forEach((lineContext) => {
                    this._updateLineStatElemInMap(map, statelem, lineContext);
                });
            });
            this.lineStatElemsByIdMap = map;
        }
        return this.lineStatElemsByIdMap;
    }

    _canvasSize(): Point {
        if (!this._canvasSizePoint) {
            this._canvasSizePoint = new Point(
                this.ctx.canvas.width,
                this.ctx.canvas.height,
            );
        }
        return this._canvasSizePoint;
    }

    _drawTitle(title: string): void {
        const fontSize = titleFontSize;
        this.ctx.font = `${fontSize}px ${titleFont}`;
        const textSize = this.ctx.measureText(title);

        this.ctx.fillStyle = titleBackgroundColour;
        this.ctx.beginPath();
        this.ctx.lineTo(0, 0);
        this.ctx.lineTo(textSize.width + fontSize, 0);
        this.ctx.lineTo(textSize.width + fontSize, fontSize + fontSize / 4);
        this.ctx.lineTo(0, fontSize + fontSize / 4);
        this.ctx.fill();

        this.ctx.fillStyle = titleFontColour;
        this.ctx.fillText(title, 10, fontSize);
    }

    _buildPathObject(coordsArray: Array<Point>): Path2D {
        const path = new Path2D();
        path.moveTo(coordsArray[0].x, coordsArray[0].y);
        coordsArray.slice(1).forEach((p) => path.lineTo(p.x, p.y));
        path.lineTo(coordsArray[0].x, coordsArray[0].y);
        path.closePath();
        return path;
    }

    _handleMouseMove = (event) => {
        const {offsetX: mouseX, offsetY: mouseY} = event;
        const {width: actualWidth, height: actualHeight} = this.ctx.canvas.getBoundingClientRect();
        const scale = this._canvasSize();
        const point = new Point(mouseX, mouseY, new Point(actualWidth, actualHeight)).asScale(scale);
        const labelX = mouseX + this.ctx.canvas.offsetLeft;
        const labelY = mouseY + this.ctx.canvas.offsetTop;

        const elems = new StatElems(scale, this.mouseOverArray.map((e) => e.elem));
        const overElem = elems.contains(point, false);
        this.setHoveredElem(overElem);
        elems.elems.forEach((e) => {e.highlight = false});

        if (!!overElem) {
            overElem.highlight = true;

            // highlight the same line in other directions i.e. pass by lines
            if (Object.getPrototypeOf(overElem) === Curve.prototype)
                elems.curves.forEach((l) => {
                    if (l.name === overElem.name)
                        l.highlight = true;
                })

            const fragment = document.createDocumentFragment();
            const {taxonomies} = this.mouseOverArray[elems.elems.indexOf(overElem)];
            Array.from(taxonomies).forEach((taxonomy, index) => {
                fragment.appendChild(document.createTextNode(taxonomy));
                if (index < taxonomies.size - 1) {
                    fragment.appendChild(document.createElement('br'));
                }
            });
            this.taxonomiesElement.innerHTML = '';
            this.taxonomiesElement.appendChild(fragment);
            this.taxonomiesElement.style.display = 'block';
            this.taxonomiesElement.style.left = `${labelX}px`;
            this.taxonomiesElement.style.top = `${labelY}px`;
        } else {
            this.taxonomiesElement.style.display = 'none';
        }
        this.renderImage({reset: false});
        elems.render(this.ctx);
    };

    _handleMouseLeave = () => {
        this.taxonomiesElement.style.display = 'none';
        this.setHoveredElem(null);
    };

    _removeMouseOverEvents(): void {
        this.ctx.canvas.removeEventListener('mousemove', this._handleMouseMove);
        this.ctx.canvas.removeEventListener('mouseleave', this._handleMouseLeave);
    }

    addMouseOverEvents(): void {
        if (!this.taxonomiesElement) return;

        this.ctx.canvas.addEventListener('mousemove', this._handleMouseMove);
        this.ctx.canvas.addEventListener('mouseleave', this._handleMouseLeave);
    }

    unmount(): void {
        this._removeMouseOverEvents();
        this.ctx = null;
        this.canvas = null;
    }

    async renderImage({url = null, reset = true}): Promise<void> {
        if (!this.image || (!!url && this.url !== url)) {
            this.url = url ?? this.url;
            this.image = new Image();
            this.image.src = this.url;

            const loadImage = (img: HTMLImageElement): Promise<void> => {
                return new Promise((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = () =>
                        reject(new Error('StatelemsRenderer: Failed to load image'));
                });
            };

            await loadImage(this.image);
        }
        this.mouseOverArray = reset ? [] : this.mouseOverArray;
        this.ctx.canvas.width = this.image.width;
        this.ctx.canvas.height = this.image.height;
        this.ctx.drawImage(this.image, 0, 0);
        if (this.locationName) {
            this._drawTitle(this.locationName);
        }
        return true;
    }

    renderLineStatelem(
        lineStatElem: LineStatElems,
        direction: string,
        highlight: boolean,
    ): LabelBounds | null {
        const curve = Curve.fromApi({
            scale: this._canvasSize(),
            name: lineStatElem.line_id,
            ...lineStatElem,
        });

        if (direction === "out") {
            curve.flip();
        }

        curve.highlight = highlight;
        curve.render(this.ctx);
        return curve;
    }

    renderLineStatElemGroup(
        lineStatElem: LineStatElemWithTaxonomies,
        highlightTaxonomy: string | null,
    ): void {
        const {statelems, taxonomies} = lineStatElem;
        statelems.forEach((statelem) => {
            const {direction} = statelem;

            const line = this.renderLineStatelem(
                statelem,
                direction,
                taxonomies.has(highlightTaxonomy),
            );
            line.taxonomies = taxonomies;

            this.mouseOverArray.push({
                elem: line,
                taxonomies: taxonomies,
            });
        });
    }

    renderAllDistinctLineStatElems(highlightTaxonomy: string | null): void {
        this.getAllLineStatElemIds().forEach((id) => {
            const lineStatElem = this._getLineStatElemsMap().get(id);
            this.renderLineStatElemGroup(lineStatElem, highlightTaxonomy);
        });
    }

    getAllLineStatElemIds(): string[] {
        return Array.from(this._getLineStatElemsMap().keys());
    }

    renderAreaStatelem(
        areaStatElem: AreaStatElems,
        highlight: boolean,
    ): LabelBounds | null {
        const area = Area.fromApi({
            scale: this._canvasSize(),
            name: areaStatElem.area_id,
            inverted: false,
            poly: JSON.parse(areaStatElem.poly),
            ignore: false,
        });

        area.highlight = highlight;
        area.render(this.ctx);
        return area;
    }

    renderAllDistinctAreaStatElems(highlightTaxonomy: string | null): void {
        this.getAllAreaStatElemAreaIds().forEach((id) => {
            const {
                taxonomies,
                statelem,
            } = this._getAreaStatElemsMap().get(id);
            const area = this.renderAreaStatelem(
                statelem,
                taxonomies.has(highlightTaxonomy),
            );
            area.taxonomies = taxonomies;
            this.mouseOverArray.push({
                elem: area,
                taxonomies: taxonomies,
            });
        });
    }

    getAllAreaStatElemAreaIds(): string[] {
        return Array.from(this._getAreaStatElemsMap().keys());
    }

    getAllLineTaxonomies(): Set<string> {
        const taxonomies = new Set();
        this.getAllLineStatElemIds().forEach((id) => {
            const lineStatElem = this._getLineStatElemsMap().get(id);
            if (lineStatElem) {
                lineStatElem.taxonomies.forEach((taxonomy) => taxonomies.add(taxonomy));
            }
        });
        return Array.from(taxonomies);
    }

    getAllAreaTaxonomies(): Set<string> {
        const taxonomies = new Set();
        this.getAllAreaStatElemAreaIds().forEach((id) => {
            const areaStatElem = this._getAreaStatElemsMap().get(id);
            if (areaStatElem) {
                areaStatElem.taxonomies.forEach((taxonomy) => taxonomies.add(taxonomy));
            }
        });
        return Array.from(taxonomies);
    }
}

export {StatelemsRenderer};
